import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken } from '../services/api';
import { toast } from 'react-toastify';
import { trackAuth, setUserId } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      // Check both localStorage and sessionStorage for token (iOS Safari compatibility)
      // sessionStorage is more reliable on iOS during OAuth redirects
      let token = localStorage.getItem('token') || sessionStorage.getItem('token') || localStorage.getItem('oauth_token') || sessionStorage.getItem('oauth_token');
      
      // If we found token in sessionStorage but not localStorage, sync it
      if (sessionStorage.getItem('token') && !localStorage.getItem('token')) {
        const sessionToken = sessionStorage.getItem('token');
        localStorage.setItem('token', sessionToken);
        setAuthToken(sessionToken);
        token = sessionToken;
      } else if (token) {
        setAuthToken(token);
      }
      
      // First, try to restore user from storage (for faster initial render)
      // Check both localStorage and sessionStorage (iOS Safari compatibility)
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      
      if (storedUser && token) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          
          // Sync user data between storages
          if (sessionStorage.getItem('user') && !localStorage.getItem('user')) {
            localStorage.setItem('user', storedUser);
          } else if (localStorage.getItem('user') && !sessionStorage.getItem('user')) {
            sessionStorage.setItem('user', storedUser);
          }
          
          // Still fetch fresh data in background, but don't block on it
          authAPI.getProfile().then(response => {
            setUser(response.data);
            const userJson = JSON.stringify(response.data);
            localStorage.setItem('user', userJson);
            sessionStorage.setItem('user', userJson);
          }).catch(error => {
            console.error('Failed to refresh user profile:', error);
            // If token is invalid, clear everything
            if (error.response?.status === 401) {
              setAuthToken(null);
              setUser(null);
              localStorage.removeItem('token');
              localStorage.removeItem('oauth_token');
              localStorage.removeItem('user');
              sessionStorage.removeItem('token');
              sessionStorage.removeItem('oauth_token');
              sessionStorage.removeItem('user');
            }
          });
          setLoading(false);
          return;
        } catch (e) {
          console.error('Failed to parse stored user:', e);
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
        }
      }
      
      // If no stored user or no token, fetch from API
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          const userJson = JSON.stringify(response.data);
          localStorage.setItem('user', userJson);
          sessionStorage.setItem('user', userJson);
        } catch (error) {
          console.error('Failed to get user profile:', error);
          // Clear invalid token
          setAuthToken(null);
          localStorage.removeItem('token');
          localStorage.removeItem('oauth_token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('oauth_token');
          sessionStorage.removeItem('user');
        }
      } else if (storedUser) {
        // If we have stored user but no token, clear it (stale data)
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);
      const { access_token } = response.data;
      
      // Set token
      setAuthToken(access_token);
      
      // Get user profile
      const profileResponse = await authAPI.getProfile();
      setUser(profileResponse.data);
      
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(profileResponse.data));
      
      // Set user ID for Google Analytics
      setUserId(profileResponse.data.id);
      
      toast.success('Welcome back!');
      return { success: true, user: profileResponse.data };
    } catch (error) {
      console.error('Login error:', error);
      let message = 'Invalid email or password. Please try again.';
      
      // Handle different error types
      if (error.response) {
        // Server responded with error
        if (error.response.status === 401) {
          message = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.response.status === 422) {
          message = error.response.data?.detail || 'Please check your email and password format.';
        } else if (error.response.data?.detail) {
          message = error.response.data.detail;
        }
      } else if (error.request) {
        // Request made but no response
        message = 'Unable to connect to server. Please check your internet connection.';
      }
      
      // Don't show toast - let the form component handle display
      // toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      await authAPI.register(userData);
      
      // Auto-login after registration
      const loginResult = await login({
        email: userData.email,
        password: userData.password,
      });
      
      if (loginResult.success) {
        toast.success('Account created successfully!');
      }
      
      return loginResult;
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Track logout event
    trackAuth.logout();
    
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('oauth_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('oauth_token');
    sessionStorage.removeItem('user');
    toast.success('Logged out successfully');
  };

  const updateProfile = async (userData) => {
    try {
      const response = await authAPI.updateProfile(userData);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const refreshProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 